import { BulmaNavButtons } from "Components/BulmaNavButtons";
import { Dividers } from "Components/Dividers";
import { HorizontalNote } from "Components/HorizontalNote";
import { PageNumberProps } from "Components/PageNumber";
import { Section } from "Components/Section";
import { Spacer } from "Components/Spacer";
import { WhatIsThisModal } from "Components/WhatIsThisModal";
import { Checkbox } from "Inputs/Checkbox";
import React from "react";
import { Heading, TitleProps } from "../../Components/Heading";
import { MVCForm, MVCFormServerProps } from "../MVCForm";
import { ModelStateValues } from "../Tools";
import { useDocumentTitle } from "Components/Hooks";

export function RequirementsChecklist(
  props: CoverageLimitationsSectionProps &
    RequirementsSectionProps & {
      formProps: MVCFormServerProps;

      TitleProps: TitleProps;
      PageNumberProps: PageNumberProps;
    },
) {
  const getInitialValues = (values: ModelStateValues) => {
    return {
      ...values,
    };
  };

  const title = "Requirements Checklist";
  useDocumentTitle(`${title} - ${props.TitleProps.PageTitleSuffix}`);
  return (
    <>
      <Heading {...props} title={title} />
      <MVCForm
        {...props.formProps}
        initialValues={getInitialValues(props.formProps.initialValues)}
        renderValidationSummary={errors => (
          <div className="validation-summary-errors">
            {errors["UncheckedBoxError"]}
          </div>
        )}
      >
        {({ isSubmitting }) => (
          <div>
            <HorizontalNote>
              Please review the statements below and check each box to verify
              that the corresponding statement is true.
            </HorizontalNote>
            <Dividers>
              <CoverageLimitationsSection {...props} />
              <RequirementsSection {...props} />
              {props.HasOpenWaterCoverage && <OpenWaterRequirementsSection />}
            </Dividers>
            <Spacer height="1em" />
            <BulmaNavButtons
              backCaption="Back"
              continueCaption="Continue"
              isDisabled={isSubmitting}
            />
          </div>
        )}
      </MVCForm>
    </>
  );
}

interface CoverageLimitationsSectionProps {
  HasOpenWaterCoverage: boolean;
  IsSupGuideOrSupInstructor: boolean;
  IncludesYogaCoverage: boolean;
}

function CoverageLimitationsSection(props: CoverageLimitationsSectionProps) {
  return (
    <Section title="Coverage Limitations">
      <Dividers divider={<Spacer />}>
        <Checkbox fieldName="ClosedWaterAcknowledgement">
          I understand that Closed Water locations include: Marinas; Pools;
          Lakes; Bayou; Rivers (Grade 1 rivers only – no white water rapids).
        </Checkbox>
        {props.HasOpenWaterCoverage && (
          <Checkbox fieldName="OpenWaterAcknowledgement">
            I understand that Open Water locations include: Oceans; Sea;
            Estuaries leading out to open water.
          </Checkbox>
        )}
        <Checkbox fieldName="IneligibleEntitiesAcknowledgement">
          I understand that the following are ineligible and cannot be covered:
          Certified High School / College Athletic Trainers; Coaching of
          Competitive Athletics; Instructors based outside of the U.S.; Physical
          Education Teachers working within school systems.
        </Checkbox>
        <Checkbox fieldName="ClosedWaterSessionAcknowledgement">
          I understand that coverage in each Closed Water session is limited to
          the following distances (radius) from the actual take-off point where
          instruction begins: SUP Yoga 2 miles; SUP Instructor 3 miles; SUP
          Guide 3 miles.
        </Checkbox>
        {props.HasOpenWaterCoverage && (
          <Checkbox fieldName="OpenWaterSessionAcknowledgement">
            I understand that coverage in each Open Water session is limited to
            the following distances from the actual take-off point where
            instruction begins: SUP Yoga 1 miles; SUP Instructor 3 miles; SUP
            Guide 4 miles.
          </Checkbox>
        )}
        {props.IsSupGuideOrSupInstructor && (
          <Checkbox fieldName="ClientNumberLimitAcknowledgement">
            I understand that the number of clients allowed per instructor
            during any one session is three (3) for all Open Water and Closed
            Water SUP Instruction.{" "}
            <WhatIsThisModal modalTitle="3 Students per Instructor">
              If you wish to purchase insurance for greater numbers of students
              per instructor, please contact us using the information at the
              bottom of the page.
            </WhatIsThisModal>
          </Checkbox>
        )}
        {props.IncludesYogaCoverage && (
          <Checkbox fieldName="ClientNumberLimitAcknowledgementYoga">
            I understand that the maximum number of clients allowed per
            instructor during any one Closed Water SUP Yoga Instruction class is
            10 per session. If there are more than 10 clients in a session, then
            another instructor needs to be present. Classes can be no larger
            than 20 clients per session.
          </Checkbox>
        )}
      </Dividers>
    </Section>
  );
}

interface RequirementsSectionProps {
  IsYogaOnly: boolean;
}

function RequirementsSection(props: RequirementsSectionProps) {
  return (
    <Section title="Requirements">
      <Dividers divider={<Spacer />}>
        <div>
          <span className="has-text-weight-bold">
            I understand that the following requirements apply at all times
          </span>
          :
        </div>
        <Checkbox fieldName="CprAcknowledgement" noVerticalSlop>
          Instructor CPR Certificate up-to-date
        </Checkbox>
        <Checkbox fieldName="FirstAidAcknowledgement" noVerticalSlop>
          Suitable First Aid Kits available for each session
        </Checkbox>
        <Checkbox fieldName="WhistleAcknowledgement" noVerticalSlop>
          Whistles for all SUP clients and instructors per Coast Guard
          requirements
        </Checkbox>
        <Checkbox fieldName="PersonalFloatationAcknowledgement" noVerticalSlop>
          Personal Floatation Device per Coast Guard requirements for all
          clients
        </Checkbox>
        {!props.IsYogaOnly && (
          <Checkbox fieldName="LegRopeAcknowledgement" noVerticalSlop>
            Leg rope (leash) attachment to both client and SUP board for all
            clients under Open Water instruction and Closed Water guiding
          </Checkbox>
        )}
      </Dividers>
    </Section>
  );
}

function OpenWaterRequirementsSection() {
  return (
    <Section title="Open Water Requirements">
      <Dividers divider={<Spacer />}>
        <div>
          <span className="has-text-weight-bold">
            I understand that the following requirements apply at all times{" "}
            <span className="is-italic">
              for Open Water SUP Instruction only
            </span>
          </span>
          :
        </div>
        <Checkbox fieldName="StudentsAttendedAcknowledgement">
          Students must be attended at all times
        </Checkbox>
        <Checkbox fieldName="ManualAcknowledgement">
          A Best Practices Manual must be provided to all instructors
        </Checkbox>
        <Checkbox fieldName="SurfingAcknowledgement">
          Injury or any other claims relating to instructor SUP Surfing
          Activities are not covered
        </Checkbox>
        <Checkbox fieldName="RedFlagsAcknowledgement">
          Activities in areas with posted red flags are not covered
        </Checkbox>
        <Checkbox fieldName="CoastGuardAcknowledgement">
          SUP operators must abide by U.S. Coast Guard laws and regulations with
          regard to operation of their SUPs
        </Checkbox>
      </Dividers>
    </Section>
  );
}
